/**
 * Stage-change, privacy-toggle, and delete mutations for useMemoRecords.
 * Split out of useMemoRecords.ts — see that file for the composed public
 * hook.
 */

import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { apiCall } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { submitEventsOptimistic } from '../../store/submitEventsOptimistic';
import type { Note } from './notes.types';
import type { MemoAssociationNames, MemoEntityType } from './notes.utils';

export interface UseMemoRecordsStagePrivacyMutationsParams {
  orgId: number;
  recordType: MemoEntityType;
  names: MemoAssociationNames;
  notesListKey: QueryKey;
  invalidateNoteObjectQuery: (noteEntityId: string) => void;
}

/** Stage-change, privacy-toggle, and delete mutations, sharing the query context resolved by useMemoRecordsQueries. */
export function useMemoRecordsStagePrivacyMutations({
  orgId,
  recordType,
  names,
  notesListKey,
  invalidateNoteObjectQuery,
}: UseMemoRecordsStagePrivacyMutationsParams) {
  const queryClient = useQueryClient();

  // Dedicated stage-change mutation. Deliberately SEPARATE from
  // updateRecordMutation: the notes list wires updateRecordMutation's
  // `isUpdating` / `updateError` into each NoteCard's inline-edit lifecycle
  // (scoped by `editingNoteId`). If a stage save reused that shared mutation,
  // flipping its pending/error while another card is mid-edit would spuriously
  // toggle that card's `isEditing` — closing the edit form on success (via
  // NoteCard's auto-close effect) or bleeding the stage error into its edit
  // form on failure. An isolated mutation keeps the stage-save state off the
  // edit lifecycle entirely. The stage pill drives its own optimistic UI, so
  // this mutation's pending/error don't need to surface to consumers.
  const updateRecordStageMutation = useMutation({
    mutationFn: async ({
      note,
      newStageEntityId,
      userId,
    }: {
      note: Note;
      /** Target stage entity_id; null clears the stage ("un-task"). */
      newStageEntityId: string | null;
      userId: string;
    }) => {
      // names.stage is null for the note family — notes have no stage
      // concept to write, so there is nothing to submit.
      if (!names.stage) {
        return;
      }
      const stageAssociationTypeName = names.stage;
      const originalStage = note.stage_entity_id ?? null;
      // No-op when the stage is unchanged — avoids submitting an empty batch.
      if ((newStageEntityId ?? null) === originalStage) {
        return;
      }
      // many_to_one cardinality: the wire batch stays add-only (the backend
      // cascade replaces the prior stage server-side) — fold the implicit
      // removal of the prior target into THIS batch's optimistic rollback
      // context via singleCardinalityReplacements, so both rollback paths
      // (snapshot-restore and the surgical revertAssociations path) reverse
      // it. See applySingleCardinalityReplacements for details.
      const singleCardinalityReplacements =
        newStageEntityId && originalStage
          ? [
              {
                entityId: note.entity_id,
                associationTypeName: stageAssociationTypeName,
                priorTargetEntityId: originalStage,
                newTargetEntityId: newStageEntityId,
              },
            ]
          : undefined;

      return submitEventsOptimistic({
        orgId,
        userId,
        entityTypeName: recordType,
        primaryEntityId: note.entity_id,
        fallbackSeq: note.last_event_seq,
        singleCardinalityReplacements,
        build: builder => {
          // many_to_one cardinality: the wire batch stays add-only (the
          // backend cascade replaces the prior stage server-side); the
          // implicit removal of the prior target is folded into this
          // batch's rollback context via singleCardinalityReplacements
          // above. Clearing to null emits a remove of the previous stage.
          if (newStageEntityId) {
            builder.associationAdded(
              note.entity_id,
              recordType,
              stageAssociationTypeName,
              newStageEntityId
            );
          } else if (originalStage) {
            builder.associationRemoved(
              note.entity_id,
              recordType,
              stageAssociationTypeName,
              originalStage
            );
          }
        },
        errorToast: false,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: notesListKey });
      invalidateNoteObjectQuery(variables.note.entity_id);
    },
    onError: error => {
      console.error('Failed to update note stage:', error);
    },
  });

  // Single shared entry point for the interactive stage pill, used by the
  // tasks section (via TaskCard's `onStageChange`). Centralizes the
  // stage-write payload AND the failure handling (toast + structured log).
  // Rethrows so the pill's optimistic override reverts on failure.
  const updateRecordStageMutateAsync = updateRecordStageMutation.mutateAsync;
  const updateRecordStageAsync = useCallback(
    async (note: Note, newStageEntityId: string | null, userId: string) => {
      try {
        await updateRecordStageMutateAsync({ note, newStageEntityId, userId });
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error(String(err));
        toast.error("We couldn't update that status. Try again.");
        logger.error('useMemoRecords: note stage change failed', error, {
          org_id: orgId,
          entity_id: note.entity_id,
          target_stage: newStageEntityId,
        });
        throw error;
      }
    },
    [updateRecordStageMutateAsync, orgId]
  );

  // Toggle note privacy mutation
  const togglePrivacyMutation = useMutation({
    mutationFn: async ({
      noteEntityId,
      targetPrivacy,
    }: {
      noteEntityId: string;
      targetPrivacy: 'private' | 'shared';
    }) => {
      return apiCall(
        `/api/orgs/${orgId}/crm-events/notes/${noteEntityId}/privacy/`,
        'POST',
        { target_privacy: targetPrivacy }
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: notesListKey });
      invalidateNoteObjectQuery(variables.noteEntityId);
    },
    onError: error => {
      console.error('Failed to toggle note privacy:', error);
    },
  });

  // Delete note mutation
  const deleteRecordMutation = useMutation({
    mutationFn: async ({
      noteEntityId,
      lastEventSeq,
      userId,
    }: {
      noteEntityId: string;
      lastEventSeq: number;
      userId: string;
    }) => {
      return submitEventsOptimistic({
        orgId,
        userId,
        entityTypeName: recordType,
        primaryEntityId: noteEntityId,
        fallbackSeq: lastEventSeq,
        build: builder => {
          builder.objectDeleted(noteEntityId, recordType);
        },
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: notesListKey });
      invalidateNoteObjectQuery(variables.noteEntityId);
    },
    onError: (error, { noteEntityId }) => {
      console.error('Failed to delete note', noteEntityId, error);
    },
  });

  return {
    updateRecordStageAsync,
    togglePrivacyMutation,
    deleteRecordMutation,
  };
}
